/**
 * Optimistic counselor seen-state sync for in-app notifications.
 *
 * Optimistic synchronization is guaranteed only within the current SPA session.
 * Cross-tab synchronization continues through existing polling
 * (priority-summary, messages users, assignments-pending on Classroom, etc.).
 */

import { markApplicationSeen } from 'api/applications'
import { markAssignmentSeen } from 'api/dashboardAssignments'
import { markStudentDocumentsSeen } from 'api/dashboardDocuments'
import { markStudentRecommendationsSeen } from 'api/dashboardRecommendations'
import { applicationsStore } from 'pages/ApplicationsPage/model/applicationsStore'
import { essaySummaryStore } from 'pages/ApplicationsPage/model/essaySummaryStore'
import { classroomStore } from 'pages/Classroom/model/classroomStore'
import { prioritySummaryStore } from 'shared/stores/prioritySummaryStore'
import { priorityTabsStore } from 'shared/stores/priorityTabsStore'
import { counselorSeenDedupe } from './counselorSeenDedupe'

const targetIdFromRow = row =>
	String(row?.applicationTargetId || row?.id || '').trim()

const safeDecrement = (key, amount = 1) =>
	prioritySummaryStore.optimisticDecrement(key, amount)

const safeMessagesDecrement = amount =>
	prioritySummaryStore.optimisticMessagesDecrement(amount)

export const syncNewApplicationSeen = async row => {
	if (!row?.isNewApplication) return false
	const targetId = targetIdFromRow(row)
	const rowId = String(row?.id || '').trim()
	if (!targetId || !rowId) return false
	if (!counselorSeenDedupe.tryMark('applications', targetId)) return false

	try {
		await markApplicationSeen(targetId)
	} catch (error) {
		counselorSeenDedupe.unmark('applications', targetId)
		console.error('Failed to mark application seen:', error)
		return false
	}

	applicationsStore.patchApplication(rowId, { isNewApplication: false })
	safeDecrement('newApplications', 1)
	return true
}

export const syncEssayVersionResolved = ({ versionId, essayId } = {}) => {
	const vid = String(versionId || '').trim()
	if (!vid) return false
	if (!counselorSeenDedupe.tryMark('essays', vid)) return false

	const eid = String(essayId || '').trim()
	essaySummaryStore.markEssaySeen(eid, { versionId: vid })
	priorityTabsStore.removeById('essays', vid)
	safeDecrement('essays', 1)
	return true
}

export const syncFeedbackThreadSeen = ({
	versionId,
	essayId,
	unreadMessageCount,
} = {}) => {
	const vid = String(versionId || '').trim()
	if (!vid) return false
	const unread = Math.max(0, Number(unreadMessageCount) || 0)
	// Only resolve the priority card when there were unread student replies.
	if (unread <= 0) return false
	if (!counselorSeenDedupe.tryMark('feedback', vid)) return false

	const eid = String(essayId || '').trim()
	if (eid) {
		essaySummaryStore.clearEssayUnreadFeedback(eid, { subtract: unread })
	}
	priorityTabsStore.removeById('feedback', vid)
	safeDecrement('feedback', 1)
	return true
}

export const syncStudentDocumentsSeen = (
	studentId,
	{ anchorAt, fileCountForUi } = {},
) => {
	const sid = String(studentId || '').trim()
	if (!sid) return false
	if (!counselorSeenDedupe.tryMark('documents', sid)) return false

	void fileCountForUi
	applicationsStore.patchByStudentId(sid, { newDocumentsCount: 0 })
	priorityTabsStore.removeById('documents', sid)
	safeDecrement('documents', 1)
	markStudentDocumentsSeen(sid, { anchorAt }).catch(error => {
		console.error('Failed to mark student documents seen:', error)
	})
	return true
}

export const syncStudentRecommendationsSeen = (
	studentId,
	{ anchorAt, applicationTargetId, cardId, status } = {},
) => {
	const sid = String(studentId || '').trim()
	if (!sid) return false
	const tid = String(applicationTargetId || '').trim()
	const removeId = String(cardId || (tid ? `${sid}:${tid}` : sid)).trim()
	const dedupeKey = tid || sid
	if (!counselorSeenDedupe.tryMark('recommendations', dedupeKey)) return false

	priorityTabsStore.removeById('recommendations', removeId)
	safeDecrement('recommendations', 1)
	const statusKey = String(status || '').toLowerCase()
	if (statusKey === 'requested' || statusKey === 'multiple') {
		safeDecrement('lorRequests', 1)
	}
	if (statusKey === 'uploaded' || statusKey === 'multiple') {
		safeDecrement('lorUploaded', 1)
	}
	markStudentRecommendationsSeen(sid, { anchorAt, applicationTargetId: tid || undefined }).catch(
		error => {
			console.error('Failed to mark student recommendations seen:', error)
		},
	)
	return true
}

export const syncAssignmentSeen = async (assignmentId, { onAfterSync } = {}) => {
	const id = String(assignmentId || '').trim()
	if (!id) return false
	if (!counselorSeenDedupe.tryMark('assignments', id)) return false

	try {
		await markAssignmentSeen(id)
	} catch (error) {
		counselorSeenDedupe.unmark('assignments', id)
		console.error('Failed to mark assignment seen:', error)
		return false
	}

	priorityTabsStore.removeAssignmentById(id)
	classroomStore.clearPendingForAssignment(id)
	safeDecrement('assignments', 1)
	onAfterSync?.()
	return true
}

export const syncMessagesThreadOpened = (studentId, unreadCount) => {
	const sid = String(studentId || '').trim()
	const n = Math.max(0, Number(unreadCount) || 0)
	if (!sid || n === 0) return false
	if (!counselorSeenDedupe.tryMark('messages', sid)) return false

	safeMessagesDecrement(n)
	return true
}

export const wasMessagesStudentSynced = studentId => {
	const sid = String(studentId || '').trim()
	if (!sid) return false
	return counselorSeenDedupe.hasMark('messages', sid)
}
